<script setup>
import { reactive, computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  partner: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

const isEditing = computed(() => !!props.partner?.id);

const form = reactive({
  name: props.partner?.name || '',
  kind: props.partner?.kind || 'convenio',
  active: props.partner?.active ?? true,
});

const handleSubmit = () => {
  if (!form.name) return;
  emit('submit', { ...form });
};

// H6: Focus management
const dialogRef = ref(null);
let triggerEl;
onMounted(() => {
  triggerEl = document.activeElement;
  const focusable = dialogRef.value?.querySelector(
    'input,select,button,textarea,[tabindex]:not([tabindex="-1"])'
  );
  focusable?.focus();
});
onUnmounted(() => triggerEl?.focus?.());
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    role="dialog"
    aria-modal="true"
    aria-labelledby="partner-form-title"
  >
    <div
      ref="dialogRef"
      class="bg-n-background rounded-xl shadow-xl w-full max-w-lg flex flex-col"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-strong"
      >
        <h2
          id="partner-form-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{
            isEditing
              ? t('APPOINTMENTS.PARTNERS.EDIT')
              : t('APPOINTMENTS.PARTNERS.ADD')
          }}
        </h2>
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-11"
          :aria-label="t('APPOINTMENTS.CLOSE')"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-5" aria-hidden="true" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.PARTNERS.FORM.NAME') }}
            <span class="text-n-ruby-9">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.PARTNERS.FORM.KIND') }}
          </label>
          <div class="relative">
            <select
              v-model="form.kind"
              class="!mb-0 w-full appearance-none !bg-none border border-n-strong rounded-lg pl-3 pr-9 py-2 text-sm bg-n-background text-n-slate-12 transition-colors hover:border-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option value="convenio">
                {{ t('APPOINTMENTS.PARTNERS.KIND.CONVENIO') }}
              </option>
              <option value="seguro">
                {{ t('APPOINTMENTS.PARTNERS.KIND.SEGURO') }}
              </option>
              <option value="plano">
                {{ t('APPOINTMENTS.PARTNERS.KIND.PLANO') }}
              </option>
              <option value="outros">
                {{ t('APPOINTMENTS.PARTNERS.KIND.OUTROS') }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down absolute right-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="partner-active"
            v-model="form.active"
            type="checkbox"
            class="rounded border-n-strong"
          />
          <label
            for="partner-active"
            class="text-sm text-n-slate-12 select-none cursor-pointer"
          >
            {{ t('APPOINTMENTS.PARTNERS.FORM.ACTIVE') }}
          </label>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 rounded-lg"
          @click="$emit('close')"
        >
          {{ t('APPOINTMENTS.PARTNERS.FORM.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSubmitting || !form.name"
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleSubmit"
        >
          {{ t('APPOINTMENTS.PARTNERS.FORM.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
